import { defineStore, acceptHMRUpdate } from 'pinia'

interface ClientCardState {
  // Selection state shared between `ClientsTab` (the table) and `InfoViewLayout`
  // (the client info card). The actual per-client data (person info plus the
  // four paginated streams) is cached by TanStack Query, not duplicated here.

  selectedClientId: string | null
  // Person id of the client whose card is currently shown in `InfoViewLayout`,
  // or `null` when no row is selected (card area is empty).
}

/**
 * Cross-component selection state for the client info card.
 *
 * Pinia is used here only to share the currently selected client id between
 * `ClientsTab` (clicked row → `selectClient`) and `InfoViewLayout` (renders
 * `ClientCard` when set). Caching of the actual person / payments / services /
 * balances / diseases responses is delegated to TanStack Query so that
 * navigating away from the Clients tab and back preserves both list scroll
 * and the open card without re-fetching, while still honoring the on-mount /
 * while-active staleness rules described in the plan.
 */
export const useClientCardStore = defineStore('clientCard', {
  state: (): ClientCardState => ({
    selectedClientId: null,
  }),
  getters: {
    /** Convenience flag for `v-if` guards in components that mount the card. */
    hasSelection: (state): boolean => state.selectedClientId !== null,
  },
  actions: {
    /**
     * Open the card for the given client id. Idempotent: re-selecting the
     * already-selected client is a no-op so watchers don't re-fire and the
     * card doesn't flash through its loading state.
     */
    selectClient(id: string): void {
      if (this.selectedClientId === id) return
      this.selectedClientId = id
    },
    /** Close the card (hides it from `InfoViewLayout`). */
    clearSelection(): void {
      this.selectedClientId = null
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useClientCardStore, import.meta.hot))
}
